/**
 * iter-230l — Refund History card.
 *
 * Renders Shopify's authoritative refund list joined with our local
 * Razorpay / payout-reversal status. Visible only to super_admin.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const RefundHistoryCard = ({ orderId, refreshKey = 0 }) => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await apiCall(`/api/admin/refunds/orders/${orderId}/history`);
        if (!cancelled) setData(resp);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load refund history.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (orderId) fetchHistory();
    return () => { cancelled = true; };
  }, [orderId, refreshKey, apiCall]);

  const refunds = data?.refunds || [];
  const localOnly = data?.local_only || [];

  if (loading) {
    return (
      <Card data-testid="refund-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Refund History</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-gray-500 flex items-center gap-1.5 py-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card data-testid="refund-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Refund History</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-red-600">{error}</CardContent>
      </Card>
    );
  }
  if (refunds.length === 0 && localOnly.length === 0) {
    return (
      <Card data-testid="refund-history-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Refund History</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-gray-500">No refunds yet.</CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="refund-history-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">
          Refund History ({refunds.length + localOnly.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {refunds.map(({ shopify, local }, i) => (
          <div key={i} className="border rounded-md p-2 space-y-1">
            <div className="flex items-center justify-between">
              <code className="text-[11px] text-gray-700">Shopify refund #{shopify.id}</code>
              <Badge variant="outline" className="text-[10px]">
                {shopify.created_at ? new Date(shopify.created_at).toLocaleDateString() : '—'}
              </Badge>
            </div>
            <div className="text-gray-600">
              {(shopify.refund_line_items || []).length} line(s) refunded
              {local?.amount_inr && <> • <span className="font-medium">₹{local.amount_inr}</span></>}
            </div>
            {local?.razorpay_status && (
              <div className="text-gray-500">
                Razorpay: <span className={local.razorpay_status === 'issued' ? 'text-green-700' : 'text-amber-700'}>
                  {local.razorpay_status}
                </span>
                {local.razorpay_refund_id && <> • <code>{local.razorpay_refund_id}</code></>}
              </div>
            )}
            {local?.alert && (
              <div className="flex items-center gap-1 text-red-600">
                <AlertTriangle className="h-3 w-3" /> Razorpay desync alert raised
              </div>
            )}
            {local?.reason && (
              <div className="text-gray-500 italic line-clamp-2">"{local.reason}"</div>
            )}
          </div>
        ))}

        {localOnly.length > 0 && (
          <div className="text-[11px] text-amber-700">
            {localOnly.length} local audit row(s) without a matching Shopify
            refund — may indicate a Shopify webhook miss. Investigate.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RefundHistoryCard;
